import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { companyService } from "services";
import {
  CompanyForm,
  CompanyQuery,
  CompanyUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type CompanyListKey = ["companies"] | ["companies", CompanyQuery];
type CompanyDetailKey = ["companies", "detail", number | string];

const companyKeys = {
  all: ["companies"] as const,
  list: (params?: CompanyQuery): CompanyListKey =>
    params ? ["companies", params] : ["companies"],
  detail: (id: number | string): CompanyDetailKey => [
    "companies",
    "detail",
    id,
  ],
};

export function useCompany() {
  const queryClient = useQueryClient();

  const fetchList = (params?: CompanyQuery, enabled = true) => {
    return useQuery({
      queryKey: companyKeys.list(params),
      queryFn: () => companyService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: data?.totalPage ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: companyKeys.detail(id),
      queryFn: () => companyService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createCompany = useMutation({
    mutationFn: (payload: CompanyForm) => companyService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo công ty thành công");
      }
      queryClient.invalidateQueries({ queryKey: companyKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo công ty thất bại");
      console.error("createCompany failed", err);
    },
  });

  const updateCompany = useMutation({
    mutationFn: (payload: CompanyUpdatePayload) => companyService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật công ty thành công");
      }
      queryClient.invalidateQueries({ queryKey: companyKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật công ty thất bại");
      console.error("updateCompany failed", err);
    },
  });

  const deleteCompany = useMutation({
    mutationFn: (id: number | string) => companyService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá công ty thành công");
      }
      queryClient.invalidateQueries({ queryKey: companyKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá công ty thất bại");
      console.error("deleteCompany failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createCompany,
    updateCompany,
    deleteCompany,
  };
}
